import { useEffect, useState, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ChartBottomBtn from 'components/pages/chart/ChartBottomBtn';
import DHDiagnosisPlanEvaluationChart from '../DHDiagnosisPlanEvaluationChart';
import ConfirmChartRegister from 'pages/modal/ConfirmChartRegister';
import { TDHDiagnosisPlanEvaluationChartFormValues } from 'types/chart/dhDiagnosisPlanEvaluationChart';
import { DHDiagnosisPlanEvaluationChartFormDefaultValues } from 'constants/chart/dhDiagnosisPlanEvaluationChart';
import { TPracticeChartInfo } from 'types/apis/chart/studentPracticeChart';
import {
  TEvaluationChartInfo,
  TPutEvaluationChartResponse,
} from 'types/apis/chart/studentEvaluationChart';
import useModal from 'hooks/useModal';
import useGoBack from 'hooks/useGoBack';
import { TChartInfoResponse } from 'hooks/chart/useGetChartInfo';
import useChartRegister from 'hooks/chart/useChartRegister';

type TProps = {
  query: URLSearchParams;
  chartType: number;
  category: string;
  id: number;
  isRegister: boolean;
  setSubmitEndDate: (submitEndDate: string) => void;
  chartInfoResponse: TChartInfoResponse | null;
  setChartInfoResponse: (data: any) => void;
  activeTabIndex: number;
};

const DHDiagnosisPlanEvaluationChartRegister = ({
  query,
  chartType,
  category,
  id,
  isRegister,
  setSubmitEndDate,
  chartInfoResponse,
  setChartInfoResponse,
  activeTabIndex,
}: TProps) => {
  const { openModal } = useModal();
  const goBack = useGoBack();

  const [isLoading, setIsLoading] = useState(true);

  const [isEdit, setIsEdit] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const { chartRegister } = useChartRegister(({ data }) => afterAutoSave(data));
  const methods = useForm<TDHDiagnosisPlanEvaluationChartFormValues>({
    defaultValues: DHDiagnosisPlanEvaluationChartFormDefaultValues,
  });
  const { handleSubmit, setValue, watch, reset } = methods;
  const formValues = watch();
  const formValuesRef = useRef(formValues);
  const prevFormValuesRef =
    useRef<TDHDiagnosisPlanEvaluationChartFormValues | null>(null);

  // 실습용 - 자동 저장 (20초 간격)
  useEffect(() => {
    if (isLoading) return;
    prevFormValuesRef.current = formValues;
  }, [isLoading]);

  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (!isLoading && isRegister && category === 'evaluation') {
      intervalId = setInterval(() => {
        if (isReadOnly) return;
        // 초기 상태 자동 저장 방지
        const chartInfoList = chartInfoResponse?.chartInfo as
          | TEvaluationChartInfo[]
          | undefined;
        if (
          chartInfoList &&
          chartInfoList.length > (activeTabIndex > 1 ? 1 : 0)
        ) {
          const currentChartInfo = chartInfoList[activeTabIndex > 1 ? 1 : 0];
          if (
            currentChartInfo?.chartData === null &&
            JSON.stringify(DHDiagnosisPlanEvaluationChartFormDefaultValues) ===
              JSON.stringify(formValuesRef.current)
          ) {
            return;
          }
        }

        // 이전 값과 비교하여 변경된 경우만 자동 저장 요청
        if (
          JSON.stringify(formValuesRef.current) !==
          JSON.stringify(prevFormValuesRef.current)
        ) {
          onValid(true, formValuesRef.current);
        }
      }, 20000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRegister, isLoading, category, isReadOnly]);

  const afterAutoSave = ({ chartInfo }: TPutEvaluationChartResponse) => {
    setChartInfoResponse((prev: any) => {
      if (!prev) return prev;
      const newChartInfo = prev.chartInfo as TEvaluationChartInfo[];
      const newChartInfoData = [...newChartInfo];
      newChartInfoData[activeTabIndex > 1 ? 1 : 0] = {
        ...newChartInfoData[activeTabIndex > 1 ? 1 : 0],
        chartData: chartInfo?.chartData || null,
        fileInfoList: chartInfo?.fileInfoList || [],
      };
      return {
        ...prev,
        chartInfo: newChartInfoData,
      };
    });
    prevFormValuesRef.current = chartInfo?.chartData || null;
  };

  // 차트 조회
  useEffect(() => {
    if (!chartInfoResponse) return;
    const chartInfo = (chartInfoResponse.chartInfo as TEvaluationChartInfo[])[
      activeTabIndex > 1 ? 1 : 0
    ];
    setIsLoading(true);
    setChartInfo(chartInfo);
  }, [activeTabIndex]);

  const fetchError = () =>
    openModal({
      title: '차트 조회 실패',
      text: '다시 시도해 주세요.',
      onSubmit: goBackToList,
      onClickOutside: goBackToList,
    });

  const checkSubmitEndDate = (submitEndDate: string) => {
    if (category === 'practice' || !submitEndDate) return;
    const isoFormattedDate = submitEndDate.replace(/\./g, '-');
    const nextDate = new Date(isoFormattedDate);

    if (isNaN(nextDate.getTime())) {
      fetchError();
      return;
    }
    nextDate.setDate(nextDate.getDate() + 1);
    setIsReadOnly(nextDate < new Date());
  };

  const goBackToList = () =>
    goBack({
      patientId: query.get('patientId') || undefined,
      courseInfoId: query.get('courseInfoId') || undefined,
      chartRound: query.get('chartRound') || undefined,
    });

  const handleSuccessGetChartInfo = (data: TChartInfoResponse) => {
    if (!data) {
      setIsLoading(false);
      return;
    }
    setChartInfoResponse(data);

    const chartInfo =
      category === 'practice'
        ? (data.chartInfo as TPracticeChartInfo)
        : (data.chartInfo as TEvaluationChartInfo[])[
            activeTabIndex > 1 ? 1 : 0
          ];
    if (!chartInfo) {
      fetchError();
      return;
    }
    const chartData: TDHDiagnosisPlanEvaluationChartFormValues | null =
      chartInfo.chartData as TDHDiagnosisPlanEvaluationChartFormValues | null;

    setIsEdit(!!chartInfo);
    setChartInfo(chartInfo);
  };

  const setChartInfo = (
    chartInfo: TPracticeChartInfo | TEvaluationChartInfo
  ) => {
    const chartData: TDHDiagnosisPlanEvaluationChartFormValues | null =
      (chartInfo?.chartData as TDHDiagnosisPlanEvaluationChartFormValues | null) ||
      null;

    if (category !== 'practice') {
      const submitEndDate =
        (chartInfo as TEvaluationChartInfo)?.submitEndDate || '';
      setSubmitEndDate(submitEndDate);
      checkSubmitEndDate(submitEndDate);
    }
    if (!chartData) {
      reset();
      setIsLoading(false);
      return;
    }
    Object.keys(chartData).forEach((key) => {
      setValue(
        key as keyof TDHDiagnosisPlanEvaluationChartFormValues,
        chartData[key as keyof TDHDiagnosisPlanEvaluationChartFormValues]
      );
    });
    setIsLoading(false);
  };

  useEffect(() => {
    if (chartInfoResponse) {
      handleSuccessGetChartInfo(chartInfoResponse);
    }
  }, []);

  // 차트 등록
  const onValid = (
    isAutoSave: boolean,
    data: TDHDiagnosisPlanEvaluationChartFormValues
  ) => {
    const formData = new FormData();

    formData.append(
      'request',
      new Blob(
        [
          JSON.stringify({
            chartType,
            chartData: data,
          }),
        ],
        {
          type: 'application/json',
        }
      )
    );
    chartRegister({
      category,
      id: id,
      data: formData,
      isEdit: isEdit,
      isAutoSave,
      patientId: query.get('patientId') || undefined,
      courseInfoId: query.get('courseInfoId') || undefined,
      chartRound: query.get('chartRound') || undefined,
      onLoading: setIsLoading,
      onConfirm: () => {
        openModal({
          element: (
            <ConfirmChartRegister
              category={category}
              chartType={chartType}
              id={id}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              chartName={chartInfoResponse?.chartName}
              data={formData}
            />
          ),
        });
      },
    });
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit((data) => onValid(false, data))}>
          <DHDiagnosisPlanEvaluationChart
            isRegister={isRegister}
            isReadOnly={isReadOnly}
          />
          {(Number(query.get('tab')) === 0 ||
            Number(query.get('tab')) === 2) && (
            <ChartBottomBtn
              category={category}
              id={id}
              courseId={query.get('courseId') || undefined}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              studentUserId={query.get('studentUserId') || undefined}
              isRegister={isRegister}
              isEdit={isEdit}
              isReadOnly={isReadOnly}
              isChart={true}
              chartName={chartInfoResponse?.chartName}
            />
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default DHDiagnosisPlanEvaluationChartRegister;
